import * as net from 'net';

/**
 * TCP free-port detection by *binding* a server — the authoritative test for
 * "can I actually take this port right now". (Contrast with a connect-probe,
 * which only detects a port that is already LISTENING.)
 *
 * Always binds IPv4 127.0.0.1 (never `::`) so dual-stack does not double-count.
 */

export interface FreePortOptions {
  /** Interface to bind. Default '127.0.0.1' (avoids IPv6 ambiguity on Windows). */
  host?: string;
  /** Ports to probe upward from each base before giving up. Default 200. */
  maxScan?: number;
}

const DEFAULT_HOST = '127.0.0.1';
const DEFAULT_MAX_SCAN = 200;
const MAX_PORT = 65535;

/**
 * Resolve true if `port` can be bound right now (i.e. it is free), false if it
 * is occupied/reserved (EADDRINUSE / EACCES / any bind error).
 */
export function isPortFree(port: number, host: string = DEFAULT_HOST): Promise<boolean> {
  return new Promise((resolve) => {
    const srv = net.createServer();
    srv.once('error', () => resolve(false));
    srv.once('listening', () => {
      srv.close(() => resolve(true));
    });
    try {
      srv.listen(port, host);
    } catch {
      resolve(false);
    }
  });
}

/**
 * Return the first free port at or above `start`.
 * Throws if none is found within `maxScan` probes.
 */
export async function findFreePort(start: number, opts: FreePortOptions = {}): Promise<number> {
  const host = opts.host ?? DEFAULT_HOST;
  const maxScan = opts.maxScan ?? DEFAULT_MAX_SCAN;
  for (let port = start, n = 0; n < maxScan && port <= MAX_PORT; port++, n++) {
    if (await isPortFree(port, host)) return port;
  }
  throw new Error(`No free port found in [${start}, ${start + maxScan})`);
}

/**
 * Return one DISTINCT free port per base, scanning upward from each base.
 * Order is preserved. A `taken` set guarantees distinctness even when the
 * scan ranges of two bases overlap (e.g. identical bases).
 *
 * Throws if any base cannot be satisfied within `maxScan` probes.
 */
export async function findFreePorts(starts: number[], opts: FreePortOptions = {}): Promise<number[]> {
  const host = opts.host ?? DEFAULT_HOST;
  const maxScan = opts.maxScan ?? DEFAULT_MAX_SCAN;
  const taken = new Set<number>();
  const out: number[] = [];

  for (const base of starts) {
    let chosen = -1;
    for (let port = base, n = 0; n < maxScan && port <= MAX_PORT; port++, n++) {
      if (taken.has(port)) continue;
      if (await isPortFree(port, host)) {
        chosen = port;
        break;
      }
    }
    if (chosen < 0) throw new Error(`No free port found for base ${base} within ${maxScan} probes`);
    taken.add(chosen);
    out.push(chosen);
  }

  return out;
}
